import { Box, Flex, HStack, Spinner, Text, VStack } from '@chakra-ui/react'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  FileIcon,
  FileTextIcon,
  FolderIcon,
  FolderOpenIcon,
} from 'lucide-react'
import { useState } from 'react'
import type { LogFileEntry } from '~/lib/api'

interface FileTreeProps {
  entries: LogFileEntry[]
  selectedFile: string | null
  onSelectFile: (path: string) => void
  isLoading?: boolean
}

interface FileTreeItemProps {
  entry: LogFileEntry
  level: number
  selectedFile: string | null
  onSelectFile: (path: string) => void
  expandedFolders: Set<string>
  toggleFolder: (path: string) => void
}

function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === undefined) {
    return ''
  }
  if (bytes === 0) {
    return '0 B'
  }
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`
}

function formatDate(dateString: string | null): string {
  if (!dateString) {
    return ''
  }
  try {
    const date = new Date(dateString)
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
  } catch {
    return dateString
  }
}

function FileTreeItem({
  entry,
  level,
  selectedFile,
  onSelectFile,
  expandedFolders,
  toggleFolder,
}: FileTreeItemProps) {
  const isExpanded = expandedFolders.has(entry.path)
  const isSelected = selectedFile === entry.path
  const isLogFile = entry.name.endsWith('.log')

  const handleClick = () => {
    if (entry.isDirectory) {
      toggleFolder(entry.path)
    } else {
      onSelectFile(entry.path)
    }
  }

  return (
    <VStack align="stretch" gap={0}>
      <Flex
        as="button"
        w="full"
        px={2}
        py={1.5}
        pl={level * 4 + 2}
        cursor="pointer"
        borderRadius="md"
        bg={isSelected ? 'brand.subtle' : 'transparent'}
        _hover={{ bg: isSelected ? 'brand.subtle' : 'bg.subtle' }}
        onClick={handleClick}
        align="center"
        justify="space-between"
        aria-label={entry.isDirectory ? `Folder ${entry.name}` : `File ${entry.name}`}
        aria-expanded={entry.isDirectory ? isExpanded : undefined}
      >
        <HStack gap={2}>
          {entry.isDirectory ? (
            <>
              <Box color="content.secondary" w={4}>
                {isExpanded ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
              </Box>
              <Box color="yellow.500">
                {isExpanded ? <FolderOpenIcon size={16} /> : <FolderIcon size={16} />}
              </Box>
            </>
          ) : (
            <>
              <Box w={4} />
              <Box color={isLogFile ? 'success.fg' : 'content.secondary'}>
                {isLogFile ? <FileTextIcon size={16} /> : <FileIcon size={16} />}
              </Box>
            </>
          )}
          <Text
            fontSize="sm"
            fontWeight={isSelected ? 'medium' : 'normal'}
            color={isSelected ? 'brand.solid' : undefined}
          >
            {entry.name}
          </Text>
        </HStack>
        {!entry.isDirectory && (
          <HStack gap={3} color="content.secondary" fontSize="xs">
            <Text>{formatFileSize(entry.size)}</Text>
            <Text display={{ base: 'none', md: 'block' }}>{formatDate(entry.modifiedAt)}</Text>
          </HStack>
        )}
      </Flex>
      {entry.isDirectory && isExpanded && entry.children && (
        <VStack align="stretch" gap={0}>
          {entry.children.map((child) => (
            <FileTreeItem
              key={child.path}
              entry={child}
              level={level + 1}
              selectedFile={selectedFile}
              onSelectFile={onSelectFile}
              expandedFolders={expandedFolders}
              toggleFolder={toggleFolder}
            />
          ))}
        </VStack>
      )}
    </VStack>
  )
}

export function FileTree({
  entries,
  selectedFile,
  onSelectFile,
  isLoading = false,
}: FileTreeProps) {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())

  const toggleFolder = (path: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev)
      if (next.has(path)) {
        next.delete(path)
      } else {
        next.add(path)
      }
      return next
    })
  }

  if (isLoading) {
    return (
      <Flex justify="center" align="center" py={8}>
        <Spinner size="lg" />
      </Flex>
    )
  }

  if (entries.length === 0) {
    return (
      <Flex justify="center" align="center" py={8}>
        <Text color="content.secondary">No log files found</Text>
      </Flex>
    )
  }

  return (
    <VStack align="stretch" gap={0}>
      {entries.map((entry) => (
        <FileTreeItem
          key={entry.path}
          entry={entry}
          level={0}
          selectedFile={selectedFile}
          onSelectFile={onSelectFile}
          expandedFolders={expandedFolders}
          toggleFolder={toggleFolder}
        />
      ))}
    </VStack>
  )
}
